import { Link } from 'react-router-dom';
import { ArrowLeft, Cookie, Shield, Settings, BarChart3, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function CookiePolicy() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 dark:from-slate-900 dark:to-slate-800 py-8 px-4">
      <div className="max-w-4xl mx-auto">
        <Link to="/">
          <Button variant="ghost" className="mb-6 gap-2">
            <ArrowLeft size={18} />
            Вернуться на главную
          </Button>
        </Link>
        
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-xl p-6 md:p-10">
          <div className="flex items-center gap-4 mb-8">
            <div className="p-4 bg-amber-100 dark:bg-amber-900/30 rounded-2xl">
              <Cookie className="w-10 h-10 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-slate-800 dark:text-white">
                Политика использования cookies
              </h1>
              <p className="text-slate-500 dark:text-slate-400 mt-1">
                Последнее обновление: 13.12.2025
              </p>
            </div>
          </div>

          <div className="prose prose-slate dark:prose-invert max-w-none space-y-8">
            
            {/* Введение */}
            <section>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <Cookie className="text-amber-500" size={24} />
                Что такое cookies?
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed">
                Cookies (куки) — это небольшие текстовые файлы, которые сохраняются на вашем устройстве 
                при посещении нашего сайта. Они помогают нам запоминать ваши предпочтения, 
                обеспечивать безопасность и улучшать работу сервиса.
              </p>
            </section>

            {/* Типы cookies */}
            <section className="bg-slate-50 dark:bg-slate-700/30 rounded-xl p-6">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-6">
                Какие cookies мы используем
              </h2>
              
              <div className="space-y-6">
                {/* Обязательные */}
                <div className="flex gap-4">
                  <div className="p-3 bg-green-100 dark:bg-green-900/30 rounded-xl h-fit">
                    <Shield className="w-6 h-6 text-green-600 dark:text-green-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white mb-2">
                      Обязательные cookies
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      Необходимы для работы сайта. Без них сайт не сможет функционировать корректно.
                    </p>
                    <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <li>• <strong>Аутентификация</strong> — сохранение сессии пользователя</li>
                      <li>• <strong>Безопасность</strong> — защита от несанкционированного доступа</li>
                      <li>• <strong>Настройки темы</strong> — светлая/тёмная тема интерфейса</li>
                      <li>• <strong>Согласие с cookie</strong> — ваш выбор относительно cookies</li>
                    </ul>
                  </div>
                </div>

                {/* Функциональные */}
                <div className="flex gap-4">
                  <div className="p-3 bg-blue-100 dark:bg-blue-900/30 rounded-xl h-fit">
                    <Settings className="w-6 h-6 text-blue-600 dark:text-blue-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white mb-2">
                      Функциональные cookies
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      Улучшают работу сайта и запоминают ваши предпочтения.
                    </p>
                    <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <li>• <strong>Языковые настройки</strong> — выбранный язык интерфейса</li>
                      <li>• <strong>Состояние навигации</strong> — последние открытые разделы</li>
                      <li>• <strong>Ключи шифрования</strong> — локальное хранение ключей E2E шифрования для чатов</li>
                      <li>• <strong>Настройки уведомлений</strong> — ваши предпочтения push-уведомлений</li>
                    </ul>
                  </div>
                </div>

                {/* Аналитические */}
                <div className="flex gap-4">
                  <div className="p-3 bg-purple-100 dark:bg-purple-900/30 rounded-xl h-fit">
                    <BarChart3 className="w-6 h-6 text-purple-600 dark:text-purple-400" />
                  </div>
                  <div>
                    <h3 className="font-bold text-slate-800 dark:text-white mb-2">
                      Аналитические cookies
                    </h3>
                    <p className="text-sm text-slate-600 dark:text-slate-400 mb-2">
                      Помогают нам понять, как пользователи взаимодействуют с сайтом.
                    </p>
                    <ul className="text-sm text-slate-500 dark:text-slate-400 space-y-1">
                      <li>• Статистика посещений страниц</li>
                      <li>• Информация об устройстве и браузере</li>
                      <li>• Данные о производительности загрузки</li>
                    </ul>
                  </div>
                </div>
              </div>
            </section>

            {/* localStorage */}
            <section>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white flex items-center gap-2 mb-4">
                <MessageCircle className="text-blue-500" size={24} />
                Локальное хранилище (localStorage)
              </h2>
              <p className="text-slate-600 dark:text-slate-400 leading-relaxed mb-4">
                Помимо cookies, мы используем localStorage браузера для хранения данных, 
                которые необходимы для работы приложения:
              </p>
              <div className="bg-blue-50 dark:bg-blue-900/20 rounded-xl p-4">
                <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                  <li>
                    <strong className="text-blue-600 dark:text-blue-400">Ключи E2E шифрования</strong> — 
                    приватные ключи для расшифровки личных сообщений в чате. 
                    <span className="text-amber-600 dark:text-amber-400"> Важно: при очистке localStorage вы потеряете доступ к зашифрованным сообщениям.</span>
                  </li>
                  <li>
                    <strong className="text-blue-600 dark:text-blue-400">Настройки интерфейса</strong> — 
                    тема оформления, состояние панелей.
                  </li>
                  <li>
                    <strong className="text-blue-600 dark:text-blue-400">Кэш данных</strong> — 
                    временное хранение данных для ускорения работы приложения.
                  </li>
                </ul>
              </div>
            </section>

            {/* Срок хранения */}
            <section>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">
                Срок хранения cookies
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b dark:border-slate-600">
                      <th className="text-left py-3 px-4 text-slate-800 dark:text-white">Тип</th>
                      <th className="text-left py-3 px-4 text-slate-800 dark:text-white">Срок хранения</th>
                    </tr>
                  </thead>
                  <tbody className="text-slate-600 dark:text-slate-400">
                    <tr className="border-b dark:border-slate-700">
                      <td className="py-3 px-4">Сессионные cookies</td>
                      <td className="py-3 px-4">До закрытия браузера</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="py-3 px-4">Аутентификация</td>
                      <td className="py-3 px-4">До 30 дней</td>
                    </tr>
                    <tr className="border-b dark:border-slate-700">
                      <td className="py-3 px-4">Настройки пользователя</td>
                      <td className="py-3 px-4">До 1 года</td>
                    </tr>
                    <tr>
                      <td className="py-3 px-4">Согласие с cookie</td>
                      <td className="py-3 px-4">До 1 года</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </section>

            {/* Управление cookies */}
            <section className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-6">
              <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">
                Как управлять cookies?
              </h2>
              <p className="text-slate-600 dark:text-slate-400 mb-4">
                Вы можете управлять cookies следующими способами:
              </p>
              <ul className="text-sm text-slate-600 dark:text-slate-400 space-y-2">
                <li>
                  <strong>Настройки браузера</strong> — большинство браузеров позволяют 
                  блокировать или удалять cookies через настройки приватности.
                </li>
                <li>
                  <strong>Очистка данных сайта</strong> — вы можете удалить все данные, 
                  связанные с нашим сайтом, через настройки браузера.
                </li>
                <li>
                  <strong>Режим инкогнито</strong> — при использовании приватного режима 
                  cookies автоматически удаляются после закрытия окна.
                </li>
              </ul>
              <div className="mt-4 p-3 bg-amber-100 dark:bg-amber-900/30 rounded-lg">
                <p className="text-sm text-amber-800 dark:text-amber-200">
                  <strong>⚠️ Предупреждение:</strong> Отключение обязательных cookies может привести 
                  к некорректной работе сайта. Удаление localStorage приведёт к потере 
                  ключей шифрования и невозможности прочитать старые зашифрованные сообщения.
                </p>
              </div>
            </section>

            {/* Контакты */}
            <section>
              <h2 className="text-xl font-bold text-slate-800 dark:text-white mb-4">
                Вопросы и обратная связь
              </h2>
              <p className="text-slate-600 dark:text-slate-400">
                Если у вас есть вопросы относительно использования cookies на нашем сайте, 
                вы можете обратиться к администратору системы или создать обращение в разделе 
                «Поддержка» в личном кабинете.
              </p>
            </section>

            {/* Связанные документы */}
            <section className="border-t dark:border-slate-700 pt-6">
              <h3 className="font-bold text-slate-800 dark:text-white mb-3">
                Связанные документы:
              </h3>
              <Link 
                to="/privacy-policy" 
                className="text-blue-600 hover:underline dark:text-blue-400"
              >
                Политика обработки персональных данных →
              </Link>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
}
